import type { JSX } from 'react';
import { Alert, AlertTitle, Chip, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { AsyncSection } from '../components/AsyncSection.js';
import { DataTable } from '../components/DataTable.js';

interface ConsentRow {
  id: string;
  circleId: string;
  status: string;
  type: string;
  version: number;
  grantorName: string;
  scopes: string[];
  activatedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
}

interface ConsentsDto {
  items: ConsentRow[];
  disputes: Array<{ id: string; circleId: string; summary: string; createdAt: string }>;
  canCreateConsent: false;
}

export function ConsentsPage(): JSX.Element {
  const query = useQuery({
    queryKey: ['admin', 'consents'],
    queryFn: () => api.get<ConsentsDto>('/v1/admin/consents?limit=50'),
  });

  return (
    <>
      <Typography variant="h1" gutterBottom>
        Consentimentos
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>Limite deliberado</AlertTitle>
        A operação acompanha escopo, versão e casos em análise — mas não cria consentimento,
        não representa a pessoa acompanhada e não amplia acesso.
      </Alert>
      <AsyncSection query={query} emptyMessage="Nenhum consentimento registrado.">
        {(data) => (
          <>
            <DataTable
              rows={data.items}
              getRowKey={(row) => row.id}
              emptyMessage="Nenhum consentimento registrado."
              columns={[
                { key: 'grantor', label: 'Declarante', render: (row) => row.grantorName },
                {
                  key: 'type',
                  label: 'Tipo',
                  render: (row) => (row.type === 'self' ? 'a própria pessoa' : 'representante declarado'),
                },
                {
                  key: 'status',
                  label: 'Estado',
                  render: (row) => (
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                      <Chip
                        size="small"
                        color={
                          row.status === 'active' ? 'success' : row.status === 'revoked' ? 'error' : 'warning'
                        }
                        label={row.status}
                      />
                      <Chip size="small" variant="outlined" label={`v${row.version}`} />
                    </Stack>
                  ),
                },
                { key: 'scopes', label: 'Escopos', render: (row) => row.scopes.join(', ') },
                {
                  key: 'dates',
                  label: 'Vigência',
                  render: (row) =>
                    row.revokedAt
                      ? `revogado em ${new Date(row.revokedAt).toLocaleDateString('pt-BR')}`
                      : row.expiresAt
                        ? `expira em ${new Date(row.expiresAt).toLocaleDateString('pt-BR')}`
                        : 'sem prazo declarado',
                },
              ]}
            />
            {data.disputes.length > 0 ? (
              <>
                <Typography variant="h2" sx={{ mt: 4, mb: 1 }}>
                  Casos em análise ({data.disputes.length})
                </Typography>
                <Alert severity="warning">
                  Enquanto há análise de acesso, o sistema não amplia acesso novo.
                </Alert>
                <DataTable
                  rows={data.disputes}
                  getRowKey={(row) => row.id}
                  emptyMessage="Nenhum caso em análise."
                  columns={[
                    { key: 'summary', label: 'Resumo', render: (row) => row.summary },
                    { key: 'circle', label: 'Círculo', render: (row) => row.circleId },
                    {
                      key: 'created',
                      label: 'Aberto em',
                      render: (row) => new Date(row.createdAt).toLocaleString('pt-BR'),
                    },
                  ]}
                />
              </>
            ) : null}
          </>
        )}
      </AsyncSection>
    </>
  );
}